import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Divider, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Print, ArrowBack } from '@mui/icons-material';
import { insuranceCaseService, printSettingsService } from '../../services/api';

const InsuranceInvestigationsPrint = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [packet, setPacket] = useState(null);
  const [printSettings, setPrintSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.add('consent-print-page');
    return () => document.body.classList.remove('consent-print-page');
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, psRes] = await Promise.all([
          insuranceCaseService.getPacket(caseId),
          printSettingsService.getSettings(),
        ]);
        setPacket(pRes.data);
        setPrintSettings(psRes.data);
      } catch {
        alert('Failed to load investigation reports');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [caseId]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
  );

  if (!packet) return <Box sx={{ p: 4 }}><Typography>Case not found.</Typography></Box>;

  const ps = printSettings || {};
  const mt = ps.marginTop ?? 10;
  const mb = ps.marginBottom ?? 10;
  const ml = ps.marginLeft ?? 10;
  const mr = ps.marginRight ?? 10;

  const patient = packet.patient;
  const investigations = packet.investigations || [];

  // Group by category
  const grouped = investigations.reduce((acc, inv) => {
    const cat = inv.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(inv);
    return acc;
  }, {});

  return (
    <Box>
      {/* Print controls — hidden when printing */}
      <Box className="no-print" sx={{ p: 2, display: 'flex', gap: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
        <Button startIcon={<ArrowBack />} onClick={() => (window.history.length > 1 ? navigate(-1) : window.close())}>Back</Button>
        <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}>Print</Button>
      </Box>

      {/* ── Printable document ── */}
      <Box sx={{
        maxWidth: '210mm',
        margin: '0 auto',
        p: `${mt}mm ${mr}mm ${mb}mm ${ml}mm`,
        fontFamily: '"Times New Roman", serif',
        fontSize: '13px',
        lineHeight: 1.6,
        bgcolor: 'white',
        '@media print': {
          margin: 0,
          maxWidth: 'none',
          padding: `${mt}mm ${mr}mm ${mb}mm ${ml}mm`,
          boxShadow: 'none',
        },
      }}>

        {/* Hospital header — only for non-preprinted letterheads */}
        {ps.letterheadType !== 'Pre-Printed Letterhead' && (
          <>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, fontFamily: 'inherit' }}>
                {ps.hospitalName || 'Vedanta Hospital'}
              </Typography>
              {ps.address && <Typography sx={{ fontSize: '12px', fontFamily: 'inherit' }}>{ps.address}</Typography>}
              {ps.phone && <Typography sx={{ fontSize: '12px', fontFamily: 'inherit' }}>Tel: {ps.phone}</Typography>}
            </Box>
            <Divider sx={{ mb: 2, borderColor: '#333', borderWidth: 1 }} />
          </>
        )}

        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, textDecoration: 'underline', fontFamily: 'inherit', letterSpacing: 0.5 }}>
            INVESTIGATION REPORTS SUMMARY
          </Typography>
        </Box>

        {/* Patient details */}
        <Box sx={{ mb: 3, p: 1.5, border: '1px solid #ccc' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px' }}>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Name:</strong> {patient ? `${patient.firstName} ${patient.lastName}` : '___'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Gender:</strong> {patient?.gender || '___'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Admission Date:</strong> {packet.admission?.admissionDate ? new Date(packet.admission.admissionDate).toLocaleDateString('en-IN') : '___'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit', fontSize: '13px' }}>
              <strong>Diagnosis:</strong> {packet.admission?.admissionDiagnosis || '___'}
            </Typography>
          </Box>
        </Box>

        {investigations.length === 0 ? (
          <Typography sx={{ fontFamily: 'inherit', fontSize: '13px', textAlign: 'center', py: 4 }}>
            No investigations recorded for this admission.
          </Typography>
        ) : (
          Object.entries(grouped).map(([category, items]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography sx={{ fontFamily: 'inherit', fontSize: '14px', fontWeight: 700, mb: 1 }}>
                {category}
              </Typography>
              <Table size="small" sx={{ '& th, & td': { border: '1px solid #999', fontFamily: 'inherit', fontSize: '12px', padding: '4px 6px' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Investigation</strong></TableCell>
                    <TableCell><strong>Ordered</strong></TableCell>
                    <TableCell><strong>Result</strong></TableCell>
                    <TableCell><strong>Unit</strong></TableCell>
                    <TableCell><strong>Reference Range</strong></TableCell>
                    <TableCell><strong>Flag</strong></TableCell>
                    <TableCell><strong>Result Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(inv => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.investigationName}</TableCell>
                      <TableCell>{inv.orderedAt ? new Date(inv.orderedAt).toLocaleDateString('en-IN') : '—'}</TableCell>
                      <TableCell>{inv.resultValue || '—'}</TableCell>
                      <TableCell>{inv.unit || '—'}</TableCell>
                      <TableCell>{inv.referenceRange || '—'}</TableCell>
                      <TableCell>{inv.flag || '—'}</TableCell>
                      <TableCell>{inv.resultAt ? new Date(inv.resultAt).toLocaleDateString('en-IN') : '—'}</TableCell>
                      <TableCell>{inv.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))
        )}

      </Box>{/* end printable document */}

      <style>{`
        @page {
          size: A4;
          margin: 0;
        }
        @media print {
          html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            min-height: 0 !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default InsuranceInvestigationsPrint;
